// Renderer for context pins - vertical cylinders with colored tops and labels

import * as THREE from 'three'
import type { GardenScene } from '@/scene/GardenScene'
import type { ContextPin } from '@/types/zone'
import { getPinTypeDef, PIN_RADIUS, PIN_HEIGHT, PIN_LABEL_SCALE } from '@/constants/zoneTypes'
import { t } from '@/i18n'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'

export class PinRenderer {
  private scene: GardenScene
  private group: THREE.Group
  private meshes: Map<string, THREE.Group> = new Map()

  constructor(scene: GardenScene) {
    this.scene = scene

    // Create group for pins
    this.group = new THREE.Group()
    this.group.name = 'pins'

    // Add to scene
    const threeScene = scene.getScene()
    threeScene.add(this.group)

    debug.log('PinRenderer', 'Initialized')
  }

  /**
   * Get the pins group (for layer visibility control)
   */
  getGroup(): THREE.Group {
    return this.group
  }

  /**
   * Render all pins
   */
  renderPins(pins: ContextPin[]): void {
    // Clear existing
    this.clear()

    for (const pin of pins) {
      this.renderPin(pin)
    }

    debug.log('PinRenderer', `Rendered ${pins.length} pins`)
  }

  /**
   * Render a single pin
   */
  renderPin(pin: ContextPin): void {
    const typeDef = getPinTypeDef(pin.pinType)
    const pinGroup = new THREE.Group()
    pinGroup.name = `pin_${pin.id}`

    // Create cylinder body (white/light gray)
    const bodyGeometry = new THREE.CylinderGeometry(PIN_RADIUS, PIN_RADIUS * 0.8, PIN_HEIGHT, 16)
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0xeeeeee,
      roughness: 0.5,
      metalness: 0.1,
    })
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial)
    body.position.y = PIN_HEIGHT / 2
    body.castShadow = true
    pinGroup.add(body)

    // Create colored top cap
    const topGeometry = new THREE.CylinderGeometry(PIN_RADIUS * 1.2, PIN_RADIUS, 0.1, 16)
    const topMaterial = new THREE.MeshStandardMaterial({
      color: typeDef.color,
      roughness: 0.3,
      metalness: 0.2,
    })
    const top = new THREE.Mesh(topGeometry, topMaterial)
    top.position.y = PIN_HEIGHT + 0.05
    top.castShadow = true
    pinGroup.add(top)

    // Create label sprite above the pin
    const label = pin.label || t(typeDef.labelKey, typeDef.label)
    const labelSprite = this.createLabelSprite(label, typeDef.color)
    labelSprite.position.y = PIN_HEIGHT + 0.4
    pinGroup.add(labelSprite)

    // Position the pin
    const x = pin.x * DPM_SCALE
    const z = pin.y * DPM_SCALE
    pinGroup.position.set(x, 0, z)

    // Store pin data in userData for selection
    pinGroup.userData.pinData = pin
    pinGroup.userData.pinId = pin.id

    this.group.add(pinGroup)
    this.meshes.set(pin.id, pinGroup)

    debug.log('PinRenderer', `Rendered pin: ${pin.id} (${pin.pinType})`)
  }

  /**
   * Create a text label sprite
   */
  private createLabelSprite(text: string, color: number): THREE.Sprite {
    const canvas = document.createElement('canvas')
    const size = 256
    canvas.width = size
    canvas.height = size / 2

    const ctx = canvas.getContext('2d')!

    // Background with rounded corners
    const colorStr = `#${color.toString(16).padStart(6, '0')}`
    ctx.fillStyle = colorStr
    this.roundRect(ctx, 4, 4, size - 8, size / 2 - 8, 12)
    ctx.fill()

    // Text
    ctx.fillStyle = this.getContrastColor(color)
    ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Truncate text if too long
    let displayText = text
    while (ctx.measureText(displayText).width > size - 24 && displayText.length > 3) {
      displayText = displayText.slice(0, -1)
    }
    if (displayText !== text) {
      displayText = displayText.slice(0, -1) + '...'
    }

    ctx.fillText(displayText, size / 2, size / 4)

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    })

    const sprite = new THREE.Sprite(material)
    sprite.scale.set(PIN_LABEL_SCALE * 2, PIN_LABEL_SCALE, 1)

    return sprite
  }

  /**
   * Draw rounded rectangle path
   */
  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  /**
   * Get contrasting text color (black or white) based on background
   */
  private getContrastColor(hex: number): string {
    const r = (hex >> 16) & 0xff
    const g = (hex >> 8) & 0xff
    const b = hex & 0xff
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#ffffff'
  }

  /**
   * Update a single pin.
   * Position-only changes update in-place (preserves mesh reference for selection/highlight).
   * Label/type changes trigger full re-render.
   */
  updatePin(pin: ContextPin): void {
    const existing = this.meshes.get(pin.id)
    if (existing) {
      const oldPin = existing.userData.pinData as ContextPin
      const labelChanged = pin.label !== oldPin.label || pin.pinType !== oldPin.pinType
      if (labelChanged) {
        // Full re-render needed for visual changes
        this.removePin(pin.id)
        this.renderPin(pin)
      } else {
        // Position-only: update in-place
        const x = pin.x * DPM_SCALE
        const z = pin.y * DPM_SCALE
        existing.position.set(x, 0, z)
        existing.userData.pinData = pin
      }
    } else {
      this.renderPin(pin)
    }
  }

  /**
   * Get a pin mesh by ID (for refreshing stale selection references)
   */
  getMesh(id: string): THREE.Group | undefined {
    return this.meshes.get(id)
  }

  /**
   * Remove a pin by ID
   */
  removePin(id: string): void {
    const mesh = this.meshes.get(id)
    if (mesh) {
      this.group.remove(mesh)
      this.disposeMesh(mesh)
      this.meshes.delete(id)
      debug.log('PinRenderer', `Removed pin: ${id}`)
    }
  }

  /**
   * Dispose of a mesh and its resources
   */
  private disposeMesh(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        } else if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose())
        }
      } else if (child instanceof THREE.Sprite) {
        child.material.map?.dispose()
        child.material.dispose()
      }
    })
  }

  /**
   * Clear all pins
   */
  clear(): void {
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh)
      this.disposeMesh(mesh)
    }
    this.meshes.clear()
    debug.log('PinRenderer', 'Cleared all pins')
  }

  /**
   * Get pin count
   */
  get count(): number {
    return this.meshes.size
  }

  /**
   * Set visibility of all pins
   */
  setVisible(visible: boolean): void {
    this.group.visible = visible
  }

  /**
   * Check if pins are visible
   */
  isVisible(): boolean {
    return this.group.visible
  }

  /**
   * Dispose renderer
   */
  dispose(): void {
    this.clear()
    const threeScene = this.scene.getScene()
    threeScene.remove(this.group)
    debug.log('PinRenderer', 'Disposed')
  }
}
